import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, CheckCircle, XCircle, ChevronRight, Clock,
  FileText, Download, Send, Eye, Loader2
} from 'lucide-react';

export default function TestPreviewPage({ onNavigate, params }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [test, setTest] = useState(null);
  const [showAnswers, setShowAnswers] = useState(false);
  const [error, setError] = useState(null);

  const fetchTest = useCallback(async () => {
    if (!params?.testId) {
      setError('No test ID provided');
      setLoading(false);
      return;
    }
    try {
      const res = await api.get(`/parent/tests/${params.testId}`);
      setTest(res.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch test:', err);
      setError(err.response?.data?.detail || 'Failed to load test');
    }
    setLoading(false);
  }, [api, params?.testId]);

  useEffect(() => { fetchTest(); }, [fetchTest]);

  const handleDownload = () => {
    if (!test) return;
    
    // Generate printable HTML
    const questionsHtml = test.questions?.map((q, i) => `
      <div style="margin-bottom: 20px; page-break-inside: avoid;">
        <p><strong>Q${i + 1}.</strong> ${q.question}</p>
        ${q.options ? `
          <div style="margin-left: 20px;">
            ${q.options.map((opt, j) => `<p>${String.fromCharCode(65 + j)}. ${opt}</p>`).join('')}
          </div>
        ` : ''}
      </div>
    `).join('') || '';

    const answersHtml = showAnswers && test.questions ? `
      <div style="page-break-before: always; margin-top: 30px;">
        <h2>Answer Key</h2>
        ${test.questions.map((q, i) => `<p>Q${i + 1}: ${q.correct_answer}</p>`).join('')}
      </div>
    ` : '';

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${test.subject} - ${test.test_type || 'Test'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
          h1 { color: #333; border-bottom: 2px solid #7c3aed; padding-bottom: 10px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .info { color: #666; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>${test.subject} Test</h1>
            <p class="info">Chapters: ${test.chapters?.join(', ') || 'All'} | Difficulty: ${test.difficulty || 'Medium'}</p>
          </div>
          <div class="info">
            <p>Total Questions: ${test.questions?.length || 0}</p>
            <p>Total Marks: ${test.questions?.length || 0}</p>
          </div>
        </div>
        ${questionsHtml}
        ${answersHtml}
      </body>
      </html>
    `;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${test.subject}_Test_${new Date().toISOString().split('T')[0]}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-4">
        <FileText className="w-16 h-16 text-slate-700 mb-4" />
        <p className="text-slate-400 mb-4">{error || 'Test not found'}</p>
        <button
          onClick={() => onNavigate('parent-test-generator')}
          className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2 rounded-lg"
        >
          Create New Test
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f172a] pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f172a]/95 backdrop-blur-sm border-b border-slate-800/50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('parent-test-generator')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white font-['Outfit']">{test.subject} Test</h1>
              <p className="text-xs text-slate-500">{test.questions?.length || 0} questions • {test.difficulty || 'Medium'}</p>
            </div>
          </div>
          <button
            onClick={handleDownload}
            className="bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" /> Download
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6 space-y-4">
        {/* Toggle Answers */}
        <div className="flex items-center justify-between bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <span className="text-white">Show Answers</span>
          <button
            onClick={() => setShowAnswers(!showAnswers)}
            className={`w-12 h-7 rounded-full transition-all ${showAnswers ? 'bg-violet-600' : 'bg-slate-700'}`}
          >
            <div className={`w-5 h-5 rounded-full bg-white transition-all ${showAnswers ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Questions */}
        {test.questions?.map((q, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl p-4"
          >
            <div className="flex items-start gap-3 mb-3">
              <span className="text-xs bg-violet-500/20 text-violet-400 px-2 py-1 rounded-full shrink-0">
                Q{i + 1}
              </span>
              <p className="text-white">{q.question}</p>
            </div>

            {q.options && (
              <div className="space-y-2 ml-8">
                {q.options.map((opt, j) => {
                  const isCorrect = showAnswers && opt === q.correct_answer;
                  return (
                    <div
                      key={j}
                      className={`p-2 rounded-lg text-sm flex items-center gap-2 ${
                        isCorrect 
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' 
                          : 'bg-slate-800/50 text-slate-300'
                      }`}
                    >
                      <span className="font-medium">{String.fromCharCode(65 + j)}.</span>
                      <span>{opt}</span>
                      {isCorrect && <CheckCircle className="w-4 h-4 ml-auto" />}
                    </div>
                  );
                })}
              </div>
            )}

            {showAnswers && !q.options && (
              <div className="mt-2 ml-8 p-2 bg-emerald-500/20 text-emerald-300 rounded-lg text-sm border border-emerald-500/30">
                <strong>Answer:</strong> {q.correct_answer}
              </div>
            )}

            {q.marks && (
              <p className="text-xs text-slate-500 mt-2 ml-8">[{q.marks} mark{q.marks > 1 ? 's' : ''}]</p>
            )}
          </motion.div>
        ))}

        {(!test.questions || test.questions.length === 0) && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-400">No questions in this test</p>
          </div>
        )}
      </div>
    </div>
  );
}
